import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Baby, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAccess } from '@/lib/access';
import { cn } from '@/lib/cn';
import { useSetPassengerAges } from '@/lib/disruptions/use-disruptions';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import type { PnrGroupScope } from '@/lib/pnr/scope';
import {
  AGE_RANGE,
  agesErrorKey,
  agesToSend,
  isAgedPaxType,
  type AgedPaxType,
} from '@/lib/rebooking/passenger-ages';
import type { PassengerAge, PassengerAgesResult } from '@/lib/rebooking/types';

// «Cargar edades» — Duffel and Atlas quote a child or an infant only with its exact age, and the
// airline sent none for this PNR (the providers said `passenger_ages_required`). The operator
// enters the age each child / infant will be on the travel date; rebooking saves them (audited
// with the operator) and searches the PNR again. Adults are quoted as adults and are not listed.

interface IMember {
  paxUrn: string;
  paxType: AgedPaxType;
  label: string;
}

export function PassengerAgesAction({
  scope,
  group,
  entered,
  className,
  testId = 'pnr-ages',
}: {
  scope: PnrGroupScope;
  group: Pick<DisruptionPnrGroupRow, 'caseUrn' | 'subCaseUrn' | 'pnrLabel' | 'passengers'>;
  /** Ages already entered for this PNR (the rebooking view's `passengerAges`) — the form opens with them. */
  entered?: readonly PassengerAge[] | undefined;
  /** The trigger's classes (a card's action shape). */
  className?: string | undefined;
  testId?: string | undefined;
}) {
  const { t } = useTranslation('pnr');
  const canName = useAccess().can('pii.name.reveal');
  const save = useSetPassengerAges(scope);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [result, setResult] = useState<PassengerAgesResult | null>(null);

  const members: IMember[] = useMemo(
    () =>
      group.passengers.flatMap((p, i) => {
        const type = p.paxType?.toUpperCase();
        if (!isAgedPaxType(type)) return [];
        const name = canName ? (p.fullName || `${p.firstName ?? ''} ${p.lastName ?? ''}`).trim() : '';
        return [{ paxUrn: p.paxUrn, paxType: type, label: name || t('ages.passenger', { n: i + 1 }) }];
      }),
    [group.passengers, canName, t],
  );
  if (!members.length) return null;

  const openDialog = () => {
    const known = new Map((entered ?? []).map((a) => [a.paxUrn, a.age]));
    setDraft(Object.fromEntries(members.map((m) => [m.paxUrn, known.has(m.paxUrn) ? String(known.get(m.paxUrn)) : ''])));
    setInvalid(new Set());
    setErrorKey(null);
    setResult(null);
    setOpen(true);
  };

  const submit = async () => {
    const parsed = agesToSend(members, draft);
    if (!parsed.ok) {
      setInvalid(new Set(parsed.invalid));
      return;
    }
    setInvalid(new Set());
    setErrorKey(null);
    try {
      const saved = await save.mutateAsync({ caseUrn: group.caseUrn, subCaseUrn: group.subCaseUrn, ages: parsed.ages });
      setResult(saved);
      setOpen(false);
    } catch (err) {
      setErrorKey(await agesErrorKey(err));
    }
  };

  return (
    <div className="space-y-1" data-testid={testId}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={className}
        onClick={openDialog}
        data-testid={`${testId}-open`}
      >
        <Baby className="mr-1 h-3.5 w-3.5" aria-hidden />
        {t('ages.action')}
      </Button>
      {result ? (
        <p role="status" className="text-xs text-muted-foreground" data-testid={`${testId}-result`}>
          {result.generation.count > 0
            ? t('ages.saved', { count: result.generation.count })
            : t('ages.savedNone')}
        </p>
      ) : null}

      <Dialog open={open} onOpenChange={(next) => (save.isPending ? undefined : setOpen(next))}>
        <DialogContent data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>{t('ages.title', { pnr: group.pnrLabel })}</DialogTitle>
            <DialogDescription>{t('ages.description')}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-3"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <ul className="space-y-2">
              {members.map((m) => {
                const [min, max] = AGE_RANGE[m.paxType];
                const id = `${testId}-${m.paxUrn}`;
                const bad = invalid.has(m.paxUrn);
                return (
                  <li key={m.paxUrn} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1">
                    <label htmlFor={id} className="min-w-0 text-sm">
                      <span className="block truncate font-medium">{m.label}</span>
                      <span className="block text-xs text-muted-foreground">{t(`ages.type.${m.paxType}`)}</span>
                    </label>
                    <span className="flex items-center gap-1.5 text-sm">
                      <input
                        id={id}
                        type="number"
                        inputMode="numeric"
                        min={min}
                        max={max}
                        step={1}
                        value={draft[m.paxUrn] ?? ''}
                        onChange={(e) => setDraft((d) => ({ ...d, [m.paxUrn]: e.target.value }))}
                        aria-invalid={bad || undefined}
                        aria-describedby={bad ? `${id}-error` : undefined}
                        className={cn(
                          'h-9 w-16 rounded-md border border-input bg-background px-2 text-right',
                          bad && 'border-destructive',
                        )}
                        data-testid={`${testId}-input`}
                      />
                      <span className="text-muted-foreground">{t('ages.years')}</span>
                    </span>
                    {bad ? (
                      <p id={`${id}-error`} className="col-span-2 text-xs text-destructive" data-testid={`${testId}-invalid`}>
                        {t('ages.outOfRange', { min, max })}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-muted-foreground">{t('ages.audit')}</p>
            {errorKey ? (
              <p role="alert" className="text-xs text-destructive" data-testid={`${testId}-error`}>
                {t(`ages.error.${errorKey}`)}
              </p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="ghost" size="sm" disabled={save.isPending} onClick={() => setOpen(false)}>
                {t('ages.cancel')}
              </Button>
              <Button type="submit" size="sm" disabled={save.isPending} data-testid={`${testId}-submit`}>
                {save.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                {save.isPending ? t('ages.saving') : t('ages.submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
